import { inject, Injectable, Injector, signal, WritableSignal } from '@angular/core';
import { BehaviorSubject, Observable, map } from 'rxjs';

import { StorageService } from './storage.service';
import { EncryptionService } from './encryption.service';
import { Constants } from '../shared/constants';
import { USER_TYPE } from '../enums/user';
import { Endpoint } from '../shared/endpoints';
import { HttpClient } from '@angular/common/http';
import { IRequestTokenResponse, IProfileInfoPayload, IUser } from '../../auth/models/auth.model';
import { environment } from '../../../environments/environment';
import { IClient } from '../interfaces/client.interface';

@Injectable({
  providedIn: 'root',
})
export class UserProfileService {
  private ENCRYPTION_KEY = `${environment.app}-${environment.appId}`;

  private userDetail: string;
  private userType: string;
  private userProfileSource: BehaviorSubject<IUser | null>;
  userProfile: Observable<IUser | null>;
  private userTypeSource: BehaviorSubject<USER_TYPE | null>;
  userTypeInfo: Observable<USER_TYPE | null>;

  // Reactive Signals
  private _userProfileSource: WritableSignal<IUser | null>;
  private _userTypeSource: WritableSignal<USER_TYPE | null>;

  private storageService = inject(StorageService);
  private encryptionService = inject(EncryptionService);
  private injector = inject(Injector);

  private get http() {
    return this.injector.get(HttpClient);
  }

  constructor() {
    try {
      this.userDetail = this.encryptionService.decrypt(
        this.ENCRYPTION_KEY,
        this.storageService.get(Constants.STORAGE_VARIABLES.USER),
        Constants.STORAGE_VARIABLES.USER,
      );
    } catch {
      this.userDetail = '';
      this.storageService.clear(Constants.STORAGE_VARIABLES.USER);
    }

    try {
      this.userType = this.encryptionService.decrypt(
        this.ENCRYPTION_KEY,
        this.storageService.get(Constants.STORAGE_VARIABLES.TENANT_USER_TYPE),
        Constants.STORAGE_VARIABLES.TENANT_USER_TYPE,
      );
    } catch {
      this.userType = '';
      this.storageService.clear(Constants.STORAGE_VARIABLES.TENANT_USER_TYPE);
    }

    const parsedUser = this.safeParse<IUser>(this.userDetail);
    const parsedUserType = this.safeParse<USER_TYPE>(this.userType);

    this.userProfileSource = new BehaviorSubject<IUser | null>(parsedUser);
    this.userProfile = this.userProfileSource.asObservable();

    this.userTypeSource = new BehaviorSubject<USER_TYPE | null>(parsedUserType);
    this.userTypeInfo = this.userTypeSource.asObservable();

    // Initialize Signals
    this._userProfileSource = signal<IUser | null>(parsedUser);
    this._userTypeSource = signal<USER_TYPE | null>(parsedUserType);
  }

  private safeParse<T>(value: string): T | null {
    if (!value || value === 'null' || value === 'undefined') {
      return null;
    }
    try {
      return JSON.parse(value) as T;
    } catch {
      return null;
    }
  }

  /**
   * Returns a reactive proxy of the user profile.
   * Components can do: `public user = this.userService.userProfileData`
   * And templates: `{{ user.firstName }}` will update automatically.
   */
  get userProfileData(): IUser | null {
    const user = this._userProfileSource();
    if (!user) return null;

    return new Proxy(user, {
      get: (target, prop) => {
        // Re-read from signal to ensure reactivity in trackable contexts (templates, effects)
        const currentUser = this._userProfileSource();
        //eslint-disable-next-line
        return (currentUser as any)?.[prop];
      },
    });
  }

  get userTypeData() {
    return this._userTypeSource();
  }

  get isCompany(): boolean {
    // Accessing the signal directly makes this getter reactive in templates
    const user = this._userProfileSource();
    return !!user?.client?.rcNumber;
  }

  get isIndividual(): boolean {
    return !this.isCompany;
  }

  updateProfile(userProfile: IUser | null) {
    this.userProfileSource.next(userProfile);
    this._userProfileSource.set(userProfile);
  }

  updateUserType(type: USER_TYPE | null) {
    this.userTypeSource.next(type);
    this._userTypeSource.set(type);
  }

  updateUserProfileSourceAndSubscribers(profile: IUser) {
    this.storageService.set(
      Constants.STORAGE_VARIABLES.USER,
      this.encryptionService.encrypt(
        this.ENCRYPTION_KEY,
        JSON.stringify(profile),
        Constants.STORAGE_VARIABLES.USER,
      ),
    );
    this.updateProfile(profile);
  }

  updateUserInfo(type: USER_TYPE, payload: IProfileInfoPayload): Observable<IRequestTokenResponse> {
    const endpoint =
      type === USER_TYPE.CLIENTS ? Endpoint.PROFILE.client_user : Endpoint.PROFILE.tenant;
    return this.http
      .patch<IRequestTokenResponse>(endpoint, payload)
      .pipe(map((response) => response));
  }

  getClient(clientId: string): Observable<IClient> {
    return this.http.get<IClient>(`${Endpoint.CLIENT.ONBOARDING.item(clientId)}`);
  }

  updateClient(clientId: string, payload: Partial<IClient>): Observable<IClient> {
    return this.http.put<IClient>(`${Endpoint.CLIENT.ONBOARDING.item(clientId)}`, payload);
  }

  updateUserTin(tin: string): Observable<IClient> {
    return this.http.put<IClient>(`${Endpoint.CLIENT.ONBOARDING.item(tin)}`, {});
  }
}
